import { cn } from '@today/ui-primitives'
import type { ChangeEvent, ReactElement } from 'react'
import { FieldShell } from '../field-shell/field-shell.view'

/** One selectable option. */
export interface SelectFieldOption {
	id: string
	text: string
}

/**
 * Props for the {@link SelectField} molecule.
 *
 * @property label - Visible label; becomes the control's accessible name.
 * @property options - The selectable options, in render order.
 * @property description - Optional helper text referenced by `aria-describedby`.
 * @property error - Optional error text; its presence marks the control invalid.
 * @property value - Controlled selection, as the chosen option's `id`.
 * @property onValueChange - Reports the chosen option's `id`.
 * @property placeholder - Shown as a non-selectable first entry while nothing is chosen.
 * @property className - Extra Tailwind classes merged onto the container.
 *
 * @example
 * ```tsx
 * <SelectField
 *   label="Country"
 *   options={[{ id: 'es', text: 'Spain' }]}
 *   value={country}
 *   onValueChange={setCountry}
 * />
 * ```
 */
export interface SelectFieldProps {
	label: string
	options: SelectFieldOption[]
	description?: string
	error?: string
	value?: string
	onValueChange?: (value: string) => void
	placeholder?: string
	className?: string
}

/**
 * Single-choice form field — {@link FieldShell} over a native `<select>`.
 *
 * @remarks
 * - **Composition:** {@link FieldShell} supplies the label, description, error
 *   and the a11y wiring, so both halves of the field contract cannot drift apart.
 * - **Why a native `<select>` rather than the `Select` atom:** the field contract
 *   requires `aria-invalid` on the control, and React Aria emits `aria-invalid`
 *   only on `Input`, `TextArea` and `Group` — never on a listbox trigger button,
 *   which is also not labellable by `<label for>`. A native `<select>` carries
 *   the whole contract natively and needs no JavaScript to be accessible. The
 *   `Select` atom remains the choice where a styled listbox popover is wanted.
 * - **Selection is the consumer's:** the chosen option's `id` is reported
 *   through `onValueChange`; pass `value` back in to control it.
 * - **Styling:** every colour, radius and size class is a `ds-*` token utility.
 *
 * @see packages/ui-components/src/select-field/select-field.stories.tsx for visual examples
 */
export function SelectField({
	label,
	options,
	description,
	error,
	value,
	onValueChange,
	placeholder,
	className,
}: SelectFieldProps): ReactElement {
	return (
		<FieldShell label={label} description={description} error={error} className={className}>
			<select
				value={value}
				onChange={(event: ChangeEvent<HTMLSelectElement>) => onValueChange?.(event.target.value)}
				className={cn(
					'h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50',
				)}
			>
				{placeholder ? (
					<option value="" disabled>
						{placeholder}
					</option>
				) : null}
				{options.map((option) => (
					<option key={option.id} value={option.id}>
						{option.text}
					</option>
				))}
			</select>
		</FieldShell>
	)
}
